import {
  List as BaseList,
  Datagrid,
  TextField,
  NumberField,
  FunctionField,
  useDataProvider,
  Button,
  Filter,
  DateInput,
  SimpleForm,
  Toolbar,
  SaveButton,
  useRefresh,
  Pagination,
  useDelete,
  useNotify,
} from "react-admin";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField as MuiTextField,
  IconButton,
  DialogActions,
  Button as MuiButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import RegexSearchInput from "../components/RegexSearchInput";
import { useEffect, useState } from "react";
import moment from "moment";

const ViewDetailsButton = ({ record }) => {
  const navigate = useNavigate();

  const handleViewClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/package/${record.id}/details`);
  };

  return (
    <IconButton onClick={handleViewClick} color="primary" size="small" title="Ver detalles">
      <VisibilityIcon />
    </IconButton>
  );
};

const DeletePackageButton = ({ record }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const profile = JSON.parse(localStorage.getItem("profile") || "{}");
  let providerId = profile?._id || null;
  if (profile.role === "admin-sucursal") {
    providerId = profile?.employer?._id || null;
  }

  const handleDelete = async (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setLoading(true);
    try {
      await dataProvider.delete("package", {
        id: record.id,
        previousData: record,
        meta: { providerId },
      });

      notify("Paquete eliminado exitosamente", { type: "success" });
      refresh();
      setConfirmOpen(false);
    } catch (error) {
      notify(`Error al eliminar paquete: ${error.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setConfirmOpen(false);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setConfirmOpen(true);
  };

  const handleDialogClick = (e) => {
    e.stopPropagation();
  };

  return (
    <>
      <IconButton onClick={handleDeleteClick} color="error" size="small" title="Eliminar paquete">
        <DeleteIcon />
      </IconButton>

      <Dialog
        open={confirmOpen}
        onClose={handleCancel}
        onClick={handleDialogClick}
        disableAutoFocus
        disableEnforceFocus
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que deseas eliminar este paquete?</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            <strong>Cliente:</strong> {record.clientName}
            <br />
            <strong>Paquete:</strong> {record.packageName}
            <br />
            <strong>Estado:</strong> {record.status}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Esta acción eliminará todas las sesiones relacionadas con este paquete y no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleCancel} disabled={loading}>
            Cancelar
          </MuiButton>
          <MuiButton onClick={handleDelete} color="error" variant="contained" disabled={loading}>
            {loading ? "Eliminando..." : "Eliminar"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CustomPagination = () => <Pagination rowsPerPageOptions={[10, 25, 50]} />;

const Filters = (props) => (
  <Filter {...props}>
    <RegexSearchInput source="clientName" label="Buscar por Nombre" alwaysOn />
    <RegexSearchInput source="packageName" label="Buscar por paquete" alwaysOn />
  </Filter>
);

const CustomToolbar = ({ onCancel }) => (
  <Toolbar>
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: 2,
        width: "100%",
        mt: 2,
      }}
    >
      <Button variant="contained" color="error" onClick={onCancel} sx={{ width: 150 }}>
        Cancelar
      </Button>
      <SaveButton label="Guardar" alwaysEnable sx={{ width: 150 }} />
    </Box>
  </Toolbar>
);

const MetricCard = ({ title, value }) => (
  <Card sx={{ minWidth: 200, flex: 1 }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" align="center">
        {title === "Ganancia Extra" && `Bs. ${value}`}
        {title !== "Ganancia Extra" && value}
      </Typography>
    </CardContent>
  </Card>
);

export const PackageList = (props) => {
  const profile = JSON.parse(localStorage.getItem("profile") || "{}");

  let providerId = profile?._id || null;

  if (profile.role === "admin-sucursal") {
    providerId = profile?.employer?._id || null;
  }

  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [stats, setStats] = useState(null);

  const [open, setOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paidPrice, setPaidPrice] = useState(0);
  const [packageOptions, setPackageOptions] = useState([]);
  const [description, setDescription] = useState("");
  const [expirationDate, setExpirationDate] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setSelectedPackage(null);
    setSelectedCustomer(null);
    setPaidPrice(0);
    setDescription("");
    setExpirationDate(null);
    setOpen(false);
  };

  const fetchAllCustomers = async (providerId) => {
    const perPage = 1000;
    let page = 1;
    let allData = [];
    let keepFetching = true;

    while (keepFetching) {
      const { data, total } = await dataProvider.getList("enterprise/customers", {
        pagination: { page, perPage },
        sort: { field: "birthdate", order: "ASC" },
        filter: { providerId },
      });

      allData = [...allData, ...data];
      page++;

      if (allData.length >= total) {
        keepFetching = false;
      }
    }
    return allData;
  };

  const handleSubmit = async () => {
    const booking = {
      currentSession: 0,
      packageInfo: {
        expirationDate: expirationDate || null,
        status: "No usado",
        isPackage: true,
      },
      paymentInfo: {
        partialPayment: paidPrice,
        sessionPayment: paidPrice,
        totalPrice: paidPrice,
      },
      status: "Finalizado",
      notes: description,
      totalEstimatedWorkMinutes: 0,
      bookingDate: new Date().toISOString(),
      provider: {
        _id: providerId,
      },
      serviceCart: [
        {
          service: {
            _id: selectedPackage?._id || null,
            name: selectedPackage?.name || "",
            unitPrice: selectedPackage?.price || 0,
            unitEstimatedWorkMinutes: selectedPackage?.unitEstimatedWorkMinutes || 0,
            variablePrice: selectedPackage?.variablePrice || false,
          },
          quantity: 1,
          price: selectedPackage?.price || 0,
          estimatedWorkMinutes: selectedPackage?.unitEstimatedWorkMinutes || 0,
        },
      ],
      customer: {
        _id: selectedCustomer?._id || null,
        fullName: selectedCustomer?.fullName || "",
        phone: selectedCustomer?.phone || "",
        email: selectedCustomer?.email || "",
      },
      createdAt: new Date(),
    };

    try {
      await dataProvider.create("booking", {
        data: booking,
      });
      refresh();
      handleClose();
    } catch (error) {
      console.error("Error al crear booking:", error);
    }
  };

  useEffect(() => {
    if (providerId && stats === null) {
      dataProvider
        .getOne("package/stats", { id: providerId })
        .then(({ data }) => {
          setStats(data);
        })
        .catch((error) => {
          console.error("Error al obtener stats:", error);
        });
    }
  }, [providerId]);

  useEffect(() => {
    if (providerId && customers.length === 0) {
      const fetchCustomers = async () => {
        try {
          const data = await fetchAllCustomers(providerId);

          const uniqueCustomers = data.reduce((acc, current) => {
            const exists = acc.find((c) => c._id === current._id);
            return exists ? acc : [...acc, current];
          }, []);

          setCustomers(uniqueCustomers);
        } catch (error) {
          console.error("❌ Error al obtener clientes:", error);
        }
      };

      fetchCustomers();
    }
  }, [providerId]);

  useEffect(() => {
    if (!packageOptions.length > 0) {
      dataProvider
        .getList("service", {
          id: providerId,
        })
        .then(({ data }) => {
          const filteredData = data.filter((service) => service?.package?.isEnabled === true);
          setPackageOptions(filteredData);
        })
        .catch((error) => {
          console.error("Error to get packages:", error);
        });
    }
  }, [dataProvider]);
  const [searchText, setSearchText] = useState("");
  return (
    <BaseList
      {...props}
      actions={false}
      filter={{ "provider._id": providerId }}
      filters={<Filters />}
      perPage={10}
      pagination={<CustomPagination />}
      sx={{
        paddingBottom: 3,
      }}
    >
      <Box display="flex" justifyContent="flex-end" px={2} py={1}>
        <Button variant="outlined" onClick={handleOpen}>
          + Crear
        </Button>
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Nueva venta de paquete</DialogTitle>
        <DialogContent>
          <SimpleForm onSubmit={handleSubmit} toolbar={<CustomToolbar onCancel={handleClose} />}>
            <Autocomplete
              fullWidth
              //multiple
              options={packageOptions}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) => setSelectedPackage(value)}
              renderInput={(params) => <MuiTextField {...params} label="Selecciona el paquete" margin="normal" />}
            />

            <Autocomplete
              fullWidth
              options={customers}
              getOptionLabel={(option) => option.fullName || ""}
              onChange={(e, value) => setSelectedCustomer(value)}
              inputValue={searchText}
              onInputChange={(e, value) => setSearchText(value)}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  {option.fullName}
                </li>
              )}
              renderInput={(params) => <MuiTextField {...params} label="Buscar cliente" margin="normal" />}
              sx={{ mt: 2 }}
            />

            <MuiTextField
              label="Precio pagado"
              type="number"
              fullWidth
              margin="normal"
              value={paidPrice}
              onChange={(e) => setPaidPrice(e.target.value)}
            />

            <DateInput
              label="Fecha de Expiración"
              source="expiration"
              value={expirationDate}
              onChange={(e) => {
                const value = e?.target?.value || "";
                if (!value) {
                  setExpirationDate(null);
                } else {
                  setExpirationDate(moment(value, "YYYY-MM-DD").toDate());
                }
              }}
              parse={(value) => (value ? moment(value, "YYYY-MM-DD").toDate() : null)}
              format={(value) => (value ? moment(value).format("YYYY-MM-DD") : "")}
              sx={{ mt: 2 }}
            />

            <MuiTextField
              label="Descripción"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mt: 0 }}
            />
          </SimpleForm>
        </DialogContent>
      </Dialog>

      <Box p={2}>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard title="Total Paquetes Vendidos" value={stats?.packagesSold || 0} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard title="Paquetes sin usar" value={stats?.packagesUnused || 0} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard title="Paquetes completados" value={stats?.packagesComplete || 0} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard title="Ganancia Extra" value={stats?.extraProfit || 0} />
          </Grid>
        </Grid>
      </Box>

      <Box px={2} overflow="auto">
        <Datagrid rowClick={false} bulkActionButtons={false}>
          <TextField source="clientName" label="Nombre Cliente" sortable={false} />
          <TextField source="packageName" label="Nombre Paquete" sortable={false} />
          <NumberField
            source="totalOfSessions"
            label="Sesiones Totales"
            sx={{ display: "flex", justifyContent: "center" }}
            sortable={false}
          />
          <NumberField
            source="used"
            label="Sesiones Utilizadas"
            sx={{ display: "flex", justifyContent: "center" }}
            sortable={false}
          />

          <NumberField
            source="remaining"
            label="Sesiones Restantes"
            sx={{ display: "flex", justifyContent: "center" }}
            sortable={false}
          />
          <FunctionField
            label="Fecha de Expiración"
            render={(record) => {
              if (!record.expiration) return "-";

              const isExpired = moment(record.expiration, "YYYY-MM-DD").isBefore(moment(), "day");
              const formattedDate = record.expiration.split("-").reverse().join("/");

              const color = isExpired ? "#d32f2f" : "#2e7d32"; // rojo o verde modernos

              return <span style={{ color, fontWeight: 600 }}>{formattedDate}</span>;
            }}
            sx={{ display: "flex", justifyContent: "center" }}
          />

          <FunctionField
            label="Estado del Paquete"
            render={(record) => {
              const status = record.status || "No usado";
              let color = "#9e9e9e";

              switch (status.toLowerCase()) {
                case "activo":
                  color = "#2e7d32";
                  break;
                case "expirado":
                  color = "#d32f2f";
                  break;
                case "completado":
                  color = "#424242";
                  break;
                case "no usado":
                  color = "#6d4c41";
                  break;
                default:
                  color = "#424242";
              }

              return <span style={{ color, fontWeight: 600 }}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
            }}
            sx={{ display: "flex", justifyContent: "center" }}
            sortable={false}
          />

          <FunctionField
            label="Acciones"
            render={(record) => (
              <Box display="flex" gap={1}>
                <ViewDetailsButton record={record} />
                <DeletePackageButton record={record} />
              </Box>
            )}
            sx={{ display: "flex", justifyContent: "center" }}
            sortable={false}
          />
        </Datagrid>
      </Box>
    </BaseList>
  );
};
